import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChat } from '../../features/conversations/context/ChatContext';
import { useToast } from '../../features/notifications';
import { Conversation, ConversationInteractiveButtons, ConversationInteractiveList, Message, ConversationParticipantRef } from '../../features/conversations/types/Conversation';
import { DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE, getConversationById, getConversationMessagesPage, toggleReaction } from '../../features/conversations/services/conversationService';
import { useConversationRealtimeSubscription } from '../../features/conversations/hooks/useConversationRealtimeSubscription';
import { useMessageComposer } from '../../features/conversations/hooks/useMessageComposer';
import { getContacts, Contact } from '../../features/contacts';
import { maskChannelIdentitiesInText } from '../../features/contacts/contactChannelUtils';
import { useContactChannelMasking } from '../../features/contacts/useContactChannelMasking';
import { useAuth } from '../../features/auth/context/AuthContext';
import { getMessageSourceContent } from './MessageSourceContent';
import { ParticipantAvatar } from './ParticipantAvatar';
import { ConversationShortcutMenu } from './ConversationShortcutMenu';
import { AiVoiceCallControl } from '../../features/conversations/components/AiVoiceCallControl';
import { ATTACHMENT_ACCEPT, formatBytes } from '../utils/conversationAttachments';
import { parseMessageContentWithEmbeddedMedia } from '../../features/conversations/utils/embeddedMediaFromText';
import { formatInteractiveSelectionReply, getInteractiveMessageSummary, getMessageTypeLabel, getQuotedMessageSnippet, idsEqual, mergeMessages, resolveConversationMeSender, truncateMessageSnippet, upsertAndSortMessages } from '../../features/conversations/utils/messageUtils';
import { isOptimisticMessageId, removeOptimisticMessages, removeRedundantOptimisticMessages, replaceOptimisticMessages } from '../../features/conversations/utils/optimisticMessages';
import { applyCachedMessagesIfOmitted } from '../../features/conversations/utils/conversationState';
import { formatTime, formatDateTimeInputValue, parseDateTimeInputValue } from '../utils/localization';
import { isInteractiveButtonAddDisabled } from '../utils/interactiveButtonLimits';
import { resolveConversationInteractiveButtonMaxCount } from '../../features/conversations/utils/interactiveButtonLimits';
import { isWhatsAppGroupConversation } from '../../features/conversations/utils/conversationClassification';
import styles from './FloatingChat.module.css';

const captionPlaceholderPattern = /^\[(image|video|document|audio message|sticker|location)\]$/i;

const getLocationCoordinatesLabel = (message: Message): string | null => {
  if (message.locationLatitude == null || message.locationLongitude == null) {
    return null;
  }

  return `${message.locationLatitude.toFixed(6)}, ${message.locationLongitude.toFixed(6)}`;
};

const getLocationHref = (message: Message): string | null => {
  if (message.locationUrl?.trim()) {
    return message.locationUrl;
  }

  const coordinates = getLocationCoordinatesLabel(message);
  if (!coordinates) {
    return null;
  }

  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(coordinates)}`;
};

type ChatTheme = 'ocean' | 'earth' | 'dusk';

const CHAT_THEME_STORAGE_PREFIX = 'chatThemePreference';

const toChatTheme = (value: string | null): ChatTheme => {
  if (value === 'earth' || value === 'dusk') {
    return value;
  }
  return 'ocean';
};

const getChatThemeStorageKey = (username?: string, tenantId?: string) => {
  const user = (username || 'anonymous').trim().toLowerCase();
  const tenant = (tenantId || 'default').trim().toLowerCase();
  return `${CHAT_THEME_STORAGE_PREFIX}:${tenant}:${user}`;
};

const chatThemeClassMap: Record<ChatTheme, string> = {
  ocean: styles.themeOcean,
  earth: styles.themeEarth,
  dusk: styles.themeDusk,
};

const isSameParticipantRef = (left: { type?: string; id?: string }, right: { type?: string; id?: string }) => {
  return String(left.type || '').toLowerCase() === String(right.type || '').toLowerCase()
    && String(left.id || '').toLowerCase() === String(right.id || '').toLowerCase();
};

const renderPendingStatusIcon = () => (
  <svg className={styles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
    <circle cx="8" cy="8" r="5.5" stroke="currentColor" strokeWidth="1.4" strokeDasharray="2.5 2.5" />
  </svg>
);

const renderStatusIcon = (status: 'sent' | 'delivered' | 'failed') => {
  if (status === 'sent') {
    return (
      <svg className={styles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <circle cx="8" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      </svg>
    );
  }

  if (status === 'delivered') {
    return (
      <svg className={styles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
        <path d="M2.5 8.5 5 11l3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M7 8.5 9.5 11 13 7.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg className={styles.statusIconSvg} viewBox="0 0 16 16" fill="none" aria-hidden="true" focusable="false">
      <path d="M4.5 4.5 11.5 11.5M11.5 4.5 4.5 11.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
};

const applyOptimisticReaction = (
  targetConversation: Conversation,
  messageId: string,
  emoji: string,
  actor: ConversationParticipantRef,
): Conversation => {
  let changed = false;

  const messages = targetConversation.messages.map(message => {
    if (message.id !== messageId) {
      return message;
    }

    const existing = message.reactions[emoji] || [];
    const alreadyReacted = existing.some(reactor => isSameParticipantRef(reactor, actor));
    const nextReactors = alreadyReacted
      ? existing.filter(reactor => !isSameParticipantRef(reactor, actor))
      : [...existing, actor];
    const nextReactions = { ...message.reactions };

    if (nextReactors.length > 0) {
      nextReactions[emoji] = nextReactors;
    } else {
      delete nextReactions[emoji];
    }

    changed = true;
    return {
      ...message,
      reactions: nextReactions,
    };
  });

  if (!changed) {
    return targetConversation;
  }

  return {
    ...targetConversation,
    messages,
  };
};

const FloatingChat: React.FC = () => {
  const { t } = useTranslation(['conversations', 'common']);
  const { selectedConversationId, isChatOpen, setIsChatOpen, isChatMinimized, setIsChatMinimized } = useChat();
  const { showToast } = useToast();
  const { auth } = useAuth();
  const maskChannelValues = useContactChannelMasking();
  const navigate = useNavigate();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOlderMessages, setLoadingOlderMessages] = useState(false);
  const [chatTheme, setChatTheme] = useState<ChatTheme>('ocean');
  const [showReactionPicker, setShowReactionPicker] = useState<string | null>(null);
  const [pendingReactionKeys, setPendingReactionKeys] = useState<Set<string>>(new Set());
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [maximizedMedia, setMaximizedMedia] = useState<{ type: 'image' | 'video'; src: string; alt?: string } | null>(null);
  const messagesPaneRef = useRef<HTMLDivElement>(null);
  const composerInputRef = useRef<HTMLTextAreaElement | null>(null);
  const notificationSound = useRef<HTMLAudioElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const highlightTimeoutRef = useRef<number | null>(null);
  const interactiveReplyHandlerRef = useRef<((message: Message, replyText: string) => Promise<void>) | null>(null);
  const shouldScrollToLatestOnOpenRef = useRef(false);
  const conversationRef = useRef<Conversation | null>(null);
  conversationRef.current = conversation;

  const scrollMessagesToBottom = useCallback(() => {
    const pane = messagesPaneRef.current;
    if (!pane) {
      return;
    }

    pane.scrollTop = pane.scrollHeight;
  }, []);

  useEffect(() => {
    if (!isChatOpen) {
      shouldScrollToLatestOnOpenRef.current = false;
      return;
    }

    if (!isChatMinimized) {
      shouldScrollToLatestOnOpenRef.current = true;
    }
  }, [isChatMinimized, isChatOpen]);

  useEffect(() => {
    if (!isChatOpen || isChatMinimized || loading || !conversation?.id || !shouldScrollToLatestOnOpenRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      scrollMessagesToBottom();
      shouldScrollToLatestOnOpenRef.current = false;
    }, 50);

    return () => window.clearTimeout(timer);
  }, [conversation?.id, conversation?.messages.length, isChatMinimized, isChatOpen, loading, scrollMessagesToBottom]);

  useEffect(() => {
    const key = getChatThemeStorageKey(auth?.username, auth?.tenantId);
    const saved = localStorage.getItem(key);
    setChatTheme(toChatTheme(saved));
  }, [auth?.username, auth?.tenantId, isChatOpen, selectedConversationId]);

  useEffect(() => {
    notificationSound.current = new Audio('/notification.mp3');
    notificationSound.current.volume = 0.5;
  }, []);

  useEffect(() => () => {
    if (highlightTimeoutRef.current !== null) {
      window.clearTimeout(highlightTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    if (!maximizedMedia) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMaximizedMedia(null);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [maximizedMedia]);

  useEffect(() => {
    const loadContacts = async () => {
      try {
        const users = await getContacts();
        setContacts(users);
      } catch {}
    };
    void loadContacts();
  }, []);

  const contactNameById = useMemo(() => {
    return new Map(contacts.map(c => [c.id, c.name]));
  }, [contacts]);

  const getParticipantDisplayName = useCallback((participant: ConversationParticipantRef) => {
    if (participant.type === 'aiAssistant') {
      return t('assistant', { ns: 'conversations' });
    }

    if (participant.type === 'lead') {
      return t('lead', { ns: 'conversations' });
    }

    const contactName = contactNameById.get(participant.id);
    if (contactName) {
      return maskChannelValues ? maskChannelIdentitiesInText(contactName) : contactName;
    }

    return t('participant', { ns: 'conversations' });
  }, [contactNameById, maskChannelValues, t]);

  const conversationDisplayTitle = useMemo(() => {
    if (!conversation) return t('chat', { ns: 'conversations' });
    const explicitTitle = (conversation.title || '').trim();
    if (explicitTitle) {
      return maskChannelValues ? maskChannelIdentitiesInText(explicitTitle) : explicitTitle;
    }

    const participantNames = conversation.participants
      .map(p => getParticipantDisplayName(p))
      .filter(Boolean);

    if (participantNames.length > 0) {
      return Array.from(new Set(participantNames)).join(', ');
    }

    return t('conversation', { ns: 'conversations' });
  }, [conversation, getParticipantDisplayName, maskChannelValues, t]);

  const handleReplyToInteractiveSelection = useCallback((message: Message, replyText: string) => {
    return interactiveReplyHandlerRef.current?.(message, replyText);
  }, []);

  const renderMessageContent = useCallback((message: Message) => {
    const caption = message.content?.trim() ?? '';
    const hasMeaningfulCaption = caption.length > 0 && !captionPlaceholderPattern.test(caption);
    const locationTitle = message.locationName?.trim() || t('sharedLocation', { ns: 'conversations' });
    const locationAddress = message.locationAddress?.trim() ?? '';
    const locationCoordinates = getLocationCoordinatesLabel(message);
    const locationHref = getLocationHref(message);
    const interactiveSummary = getInteractiveMessageSummary(message);

    if (message.messageType === 'image' && message.mediaUrl) {
      const imageUrl = message.mediaUrl;
      const imageAlt = message.fileName || t('imageMessage', { ns: 'conversations' });
      return (
        <>
          <button
            type="button"
            className={styles.mediaImageButton}
            onClick={() => setMaximizedMedia({ type: 'image', src: imageUrl, alt: imageAlt })}
            title={t('openInFullView', { ns: 'conversations' })}
          >
            <img className={styles.mediaImage} src={imageUrl} alt={imageAlt} loading="lazy" />
          </button>
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'audio' && message.mediaUrl) {
      return (
        <>
          <a className={styles.mediaLink} href={message.mediaUrl} target="_blank" rel="noreferrer">{t('openAudio', { ns: 'conversations' })}</a>
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'video' && message.mediaUrl) {
      const videoUrl = message.mediaUrl;
      return (
        <>
          <video
            className={styles.mediaPlayer}
            controls
            preload="metadata"
            src={videoUrl}
            onDoubleClick={() => setMaximizedMedia({ type: 'video', src: videoUrl })}
            title={t('openInFullView', { ns: 'conversations' })}
          >
            <a className={styles.mediaLink} href={videoUrl} target="_blank" rel="noreferrer">{t('openVideo', { ns: 'conversations' })}</a>
          </video>
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'document' && message.mediaUrl) {
      return (
        <>
          <a className={styles.mediaLink} href={message.mediaUrl} target="_blank" rel="noreferrer">
            {message.fileName || t('openDocument', { ns: 'conversations' })}
          </a>
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'sticker' && message.mediaUrl) {
      return (
        <>
          <img className={styles.stickerImage} src={message.mediaUrl} alt={message.fileName || t('stickerMessage', { ns: 'conversations' })} loading="lazy" />
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'location') {
      return (
        <>
          <div className={styles.locationCard}>
            <span className={styles.locationEyebrow}>{t('locationMessage', { ns: 'conversations' })}</span>
            <strong className={styles.locationTitle}>{locationTitle}</strong>
            {locationAddress ? <span className={styles.locationMeta}>{locationAddress}</span> : null}
            {locationCoordinates ? <span className={styles.locationMeta}>{locationCoordinates}</span> : null}
            {locationHref ? (
              <a className={styles.locationLink} href={locationHref} target="_blank" rel="noreferrer">
                {t('openLocation', { ns: 'conversations' })}
              </a>
            ) : null}
          </div>
          {hasMeaningfulCaption && <div className={styles.mediaCaption}>{message.content}</div>}
        </>
      );
    }

    if (message.messageType === 'interactive') {
      return (
        <div className={styles.interactiveCard}>
          <span className={styles.interactiveEyebrow}>{t('interactiveMessage', { ns: 'conversations' })}</span>
          {interactiveSummary?.bodyText ? <strong className={styles.interactiveBody}>{interactiveSummary.bodyText}</strong> : null}
          <div className={styles.interactiveMetaRow}>
            {interactiveSummary?.buttonText ? <span className={styles.interactiveMetaChip}>{t('interactiveButtonValue', { ns: 'conversations', value: interactiveSummary.buttonText })}</span> : null}
            {interactiveSummary?.optionCount ? <span className={styles.interactiveMetaChip}>{t('interactiveOptionsValue', { ns: 'conversations', count: interactiveSummary.optionCount })}</span> : null}
            {interactiveSummary?.buttonCount ? <span className={styles.interactiveMetaChip}>{t('interactiveButtonsValue', { ns: 'conversations', count: interactiveSummary.buttonCount })}</span> : null}
          </div>
          {message.interactiveList?.sections?.length ? (
            <div className={styles.interactiveSectionsList}>
              {message.interactiveList.sections.map((section, sectionIndex) => (
                <div key={`floating-interactive-section-${sectionIndex}`} className={styles.interactiveSectionCard}>
                  {section.title ? <span className={styles.interactiveSectionTitle}>{section.title}</span> : null}
                  {section.rows.map(row => (
                    <button
                      key={row.id}
                      type="button"
                      className={`${styles.interactiveRowCard} ${styles.interactiveRowAction}`}
                      onClick={() => void handleReplyToInteractiveSelection(message, formatInteractiveSelectionReply(row))}
                    >
                      <strong>{row.title}</strong>
                      {row.description ? <span>{row.description}</span> : null}
                    </button>
                  ))}
                </div>
              ))}
            </div>
          ) : message.interactiveButtons?.buttons?.length ? (
            <div className={styles.interactiveSectionsList}>
              <div className={styles.interactiveSectionCard}>
                {message.interactiveButtons.buttons.map(button => (
                  <button
                    key={button.id}
                    type="button"
                    className={`${styles.interactiveRowCard} ${styles.interactiveRowAction}`}
                    onClick={() => void handleReplyToInteractiveSelection(message, button.title)}
                  >
                    <strong>{button.title}</strong>
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      );
    }

    const contentSegments = parseMessageContentWithEmbeddedMedia(message.content || '');
    const hasInlineMedia = contentSegments.some(segment => segment.type === 'image' || segment.type === 'video');
    if (!hasInlineMedia) {
      return <>{message.content}</>;
    }

    return (
      <>
        {contentSegments.map((segment, index) => {
          if (segment.type === 'text') {
            return <div key={`text-${index}`}>{segment.value}</div>;
          }

          if (segment.type === 'video') {
            return (
              <video
                key={`video-${index}`}
                className={styles.mediaPlayer}
                controls
                preload="metadata"
                src={segment.url}
                onDoubleClick={() => setMaximizedMedia({ type: 'video', src: segment.url })}
                title={t('openInFullView', { ns: 'conversations' })}
              >
                <a className={styles.mediaLink} href={segment.url} target="_blank" rel="noreferrer">
                  {t('openVideo', { ns: 'conversations' })}
                </a>
              </video>
            );
          }

          const imageAlt = segment.alt || t('imageMessage', { ns: 'conversations' });
          return (
            <button
              key={`image-${index}`}
              type="button"
              className={styles.mediaImageButton}
              onClick={() => setMaximizedMedia({ type: 'image', src: segment.url, alt: imageAlt })}
              title={t('openInFullView', { ns: 'conversations' })}
            >
              <img className={styles.mediaImage} src={segment.url} alt={imageAlt} loading="lazy" />
            </button>
          );
        })}
      </>
    );
  }, [handleReplyToInteractiveSelection, t]);

  const loadConversation = useCallback(async (id: string) => {
    const cached = conversationRef.current?.id === id ? conversationRef.current : null;
    if (cached?.messages.length) {
      setLoading(false);
    } else {
      setLoading(true);
    }

    try {
      const data = await getConversationById(id, {
        messagePageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
        ifUnchangedSince: cached?.messages.length ? cached.updatedAt : undefined,
      });
      setConversation(applyCachedMessagesIfOmitted(data, cached));
    } catch (e) {
      showToast(t('failedToLoadConversation', { ns: 'conversations' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    if (selectedConversationId != null && isChatOpen) {
      loadConversation(selectedConversationId);
    }
  }, [selectedConversationId, isChatOpen, loadConversation]);

  const meSender = useMemo(() => {
    if (!conversation) return undefined;
    return resolveConversationMeSender(conversation, undefined, auth?.tenantId, auth?.employeeId);
  }, [auth?.employeeId, auth?.tenantId, conversation]);
  const realtimeConversationId = conversation?.id;

  const onMessagesCommitted = useCallback((conversationId: string, sentMessages: Message[]) => {
    if (sentMessages.length === 0) return;
    const lastTimestamp = sentMessages[sentMessages.length - 1].timestamp;

    setConversation(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: sentMessages.reduce((messages, message) => upsertAndSortMessages(messages, message), prev.messages),
        updatedAt: lastTimestamp,
      };
    });
  }, []);

  const onMessagesReconciled = useCallback((conversationId: string, replacements: Array<{ optimisticId: string; message: Message }>) => {
    if (replacements.length === 0) return;
    const lastTimestamp = replacements[replacements.length - 1].message.timestamp;

    setConversation(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: replaceOptimisticMessages(prev.messages, replacements),
        updatedAt: lastTimestamp,
      };
    });
  }, []);

  const onMessagesRemoved = useCallback((conversationId: string, messageIds: string[]) => {
    if (messageIds.length === 0) return;

    setConversation(prev => {
      if (!prev || prev.id !== conversationId) return prev;
      return {
        ...prev,
        messages: removeOptimisticMessages(prev.messages, messageIds),
      };
    });
  }, []);

  const {
    composer,
    quotedMessage,
    mentionSuggestions,
    mentionMenuOpen,
    mentionActiveIndex,
    commandSuggestions,
    commandMenuOpen,
    commandActiveIndex,
    interactiveComposerMode,
    setInteractiveComposerMode,
    interactiveListDraft,
    setInteractiveListDraft,
    interactiveButtonsDraft,
    setInteractiveButtonsDraft,
    isInteractiveComposerOpen,
    setIsInteractiveComposerOpen,
    hasInteractiveDraft,
    attachments,
    isUploaderExpanded,
    setIsUploaderExpanded,
    isRecordingAudio,
    isShortcutMenuOpen,
    setIsShortcutMenuOpen,
    dragActive,
    sendError,
    aiDraft,
    aiDraftError,
    aiDraftLoading,
    scheduledAt,
    scheduling,
    scheduleError,
    scheduledMessages,
    onScheduleAt,
    onSend,
    onGenerateAiDraft,
    onAudioRecordToggle,
    onShortcutSelectCommand,
    removeAttachment,
    onPickAttachments,
    onComposerDragOver,
    onComposerDragLeave,
    onComposerDrop,
    onComposerChange,
    onComposerSelectionChange,
    onComposerKeyDown,
    onSelectMention,
    onSelectCommand,
    onQuoteMessage,
    onReplyWithInteractiveSelection,
    onClearQuotedMessage,
    onUseAiDraft,
    onDismissAiDraft,
  } = useMessageComposer({
    conversationId: conversation?.id,
    meSender,
    fileInputRef,
    composerInputRef,
    resetKey: conversation?.id,
    onMessagesCommitted,
    onMessagesReconciled,
    onMessagesRemoved,
    onAiDraftFailure: () => showToast(t('failedToGenerateAiDraft', { ns: 'conversations' }), 'error'),
  });

  interactiveReplyHandlerRef.current = onReplyWithInteractiveSelection;

  const handleRealtimeMessageCreated = useCallback((evt: {
    conversationId: string;
    message: Message;
    timestamp: string;
  }) => {
    if (!idsEqual(evt.conversationId, realtimeConversationId)) return;
    setConversation(prev => {
      if (!prev || !idsEqual(prev.id, evt.conversationId)) return prev;
      return {
        ...prev,
        messages: upsertAndSortMessages(
          removeRedundantOptimisticMessages(prev.messages, evt.message),
          evt.message,
        ),
        updatedAt: evt.timestamp || evt.message.timestamp,
      };
    });
  }, [realtimeConversationId]);

  const handleRealtimeConversationUpdated = useCallback((evt: {
    conversationId: string;
    conversation: Conversation;
  }) => {
    if (!idsEqual(evt.conversationId, realtimeConversationId)) return;
    setConversation(prev => {
      if (!prev || !idsEqual(prev.id, evt.conversationId)) return prev;
      return {
        ...prev,
        title: evt.conversation.title,
        participants: evt.conversation.participants,
        messages: evt.conversation.messages,
        updatedAt: evt.conversation.updatedAt,
        isSigned: evt.conversation.isSigned,
        isAiAssistantMuted: evt.conversation.isAiAssistantMuted,
      };
    });
  }, [realtimeConversationId]);

  const healAfterReconnect = useCallback(async () => {
    if (!realtimeConversationId) return;
    const cached = conversationRef.current;
    const refreshed = await getConversationById(realtimeConversationId, {
      messagePageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
      ifUnchangedSince: cached?.messages.length ? cached.updatedAt : undefined,
    });
    if (!refreshed) return;
    setConversation(applyCachedMessagesIfOmitted(refreshed, cached) ?? refreshed);
  }, [realtimeConversationId]);

  const loadOlderMessages = useCallback(async (targetMessageId?: string) => {
    if (!conversation?.id || loadingOlderMessages) {
      return false;
    }

    if (targetMessageId && conversation.messages.some(message => idsEqual(message.id, targetMessageId))) {
      return true;
    }

    if (!conversation.hasMoreMessages) {
      return false;
    }

    setLoadingOlderMessages(true);

    try {
      let loadedCount = conversation.messages.length;
      let hasMoreMessages = Boolean(conversation.hasMoreMessages);
      let foundTarget = false;

      while (hasMoreMessages) {
        const pane = messagesPaneRef.current;
        const previousScrollHeight = pane?.scrollHeight ?? null;
        const previousScrollTop = pane?.scrollTop ?? null;
        const page = await getConversationMessagesPage(conversation.id, {
          loadedCount,
          pageSize: DEFAULT_CONVERSATION_MESSAGE_PAGE_SIZE,
        });

        if (!page) {
          return false;
        }

        if (targetMessageId && page.messages.some(message => idsEqual(message.id, targetMessageId))) {
          foundTarget = true;
        }

        loadedCount = page.loadedCount;
        hasMoreMessages = page.hasMoreMessages;

        setConversation(current => {
          if (!current || !idsEqual(current.id, conversation.id)) {
            return current;
          }

          return {
            ...current,
            messages: mergeMessages(current.messages, page.messages),
            totalMessageCount: page.totalCount,
            hasMoreMessages: page.hasMoreMessages,
          };
        });

        if (pane && previousScrollHeight != null && previousScrollTop != null) {
          await new Promise<void>(resolve => {
            window.requestAnimationFrame(() => {
              pane.scrollTop = previousScrollTop + (pane.scrollHeight - previousScrollHeight);
              resolve();
            });
          });
        }

        if (!targetMessageId) {
          return page.messages.length > 0;
        }

        if (foundTarget) {
          return true;
        }

        if (page.messages.length === 0) {
          return false;
        }
      }

      return foundTarget;
    } catch {
      showToast(t('failedToLoadConversation', { ns: 'conversations' }), 'error');
      return false;
    } finally {
      setLoadingOlderMessages(false);
    }
  }, [conversation, loadingOlderMessages, showToast, t]);

  useConversationRealtimeSubscription({
    enabled: isChatOpen,
    tenantId: auth?.tenantId,
    conversationId: realtimeConversationId,
    onMessageCreated: handleRealtimeMessageCreated,
    onConversationUpdated: handleRealtimeConversationUpdated,
    onReconnected: healAfterReconnect,
    onError: (message) => showToast(message, 'error'),
  });

  const onReaction = async (messageId: string, emoji: string) => {
    if (!conversation || !meSender) return;

    const conversationId = conversation.id;
    const pendingKey = `${conversationId}:${messageId}:${emoji}`;
    const snapshotBeforeToggle = conversation;
    const optimistic = applyOptimisticReaction(snapshotBeforeToggle, messageId, emoji, meSender);

    setPendingReactionKeys(current => {
      const next = new Set(current);
      next.add(pendingKey);
      return next;
    });
    setConversation(optimistic);
    setShowReactionPicker(null);

    try {
      const updated = await toggleReaction(conversationId, messageId, emoji, meSender);
      if (updated) {
        setConversation(current => (current && current.id === conversationId ? updated : current));
      }
    } catch {
      setConversation(current => (current && current.id === conversationId ? snapshotBeforeToggle : current));
      showToast(t('failedToLoadConversation', { ns: 'conversations' }), 'error');
    } finally {
      setPendingReactionKeys(current => {
        const next = new Set(current);
        next.delete(pendingKey);
        return next;
      });
    }
  };

  const [isScheduleMenuOpen, setIsScheduleMenuOpen] = useState(false);
  const [showCustomScheduleInput, setShowCustomScheduleInput] = useState(false);
  const [customScheduleValue, setCustomScheduleValue] = useState('');
  const [collapsedInteractiveSections, setCollapsedInteractiveSections] = useState<Set<number>>(new Set());
  const scheduleMenuRef = useRef<HTMLDivElement | null>(null);
  const canSend = (isInteractiveComposerOpen ? hasInteractiveDraft : (!!composer.trim() || attachments.length > 0)) && !!meSender;
  const isWhatsAppAppLine = conversation?.whatsAppConnectionKind === 'app' || Boolean(conversation && isWhatsAppGroupConversation(conversation));
  const scheduleDisabled = !canSend || scheduling || isInteractiveComposerOpen;
  const interactiveSectionCount = interactiveListDraft.sections.length;
  const interactiveOptionCount = interactiveListDraft.sections.reduce((total, section) => total + section.rows.length, 0);
  const interactiveButtonCount = interactiveButtonsDraft.buttons.length;
  const interactiveButtonMaxCount = useMemo(
    () => resolveConversationInteractiveButtonMaxCount(conversation),
    [conversation],
  );
  const isInteractiveButtonAddAtLimit = isInteractiveButtonAddDisabled(
    interactiveButtonCount,
    interactiveButtonMaxCount,
  );

  const onJumpToMessage = useCallback(async (messageId?: string | null) => {
    if (!messageId) {
      return;
    }

    const highlightMessage = (targetId: string) => {
      const element = document.getElementById(`floating-msg-${targetId}`);
      if (!element) {
        return false;
      }

      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedMessageId(targetId);

      if (highlightTimeoutRef.current !== null) {
        window.clearTimeout(highlightTimeoutRef.current);
      }

      highlightTimeoutRef.current = window.setTimeout(() => {
        setHighlightedMessageId(current => current === targetId ? null : current);
        highlightTimeoutRef.current = null;
      }, 1800);

      return true;
    };

    if (highlightMessage(messageId)) {
      return;
    }

    if (!conversation?.id) {
      return;
    }

    const loaded = await loadOlderMessages(messageId);
    if (!loaded) {
      return;
    }

    window.setTimeout(() => {
      highlightMessage(messageId);
    }, 0);
  }, [conversation?.id, loadOlderMessages]);

  const updateInteractiveList = useCallback((updater: (current: ConversationInteractiveList) => ConversationInteractiveList) => {
    setInteractiveListDraft(current => updater(current));
  }, [setInteractiveListDraft]);

  const updateInteractiveButtons = useCallback((updater: (current: ConversationInteractiveButtons) => ConversationInteractiveButtons) => {
    setInteractiveButtonsDraft(current => updater(current));
  }, [setInteractiveButtonsDraft]);

  const addInteractiveSection = useCallback(() => {
    updateInteractiveList(current => ({
      ...current,
      sections: [...current.sections, { title: '', rows: [{ id: '', title: '', description: '' }] }],
    }));
  }, [updateInteractiveList]);

  const removeInteractiveSection = useCallback((sectionIndex: number) => {
    setCollapsedInteractiveSections(current => {
      const next = new Set<number>();
      current.forEach(index => {
        if (index < sectionIndex) {
          next.add(index);
        } else if (index > sectionIndex) {
          next.add(index - 1);
        }
      });
      return next;
    });
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.filter((_, index) => index !== sectionIndex),
    }));
  }, [updateInteractiveList]);

  const toggleInteractiveSection = useCallback((sectionIndex: number) => {
    setCollapsedInteractiveSections(current => {
      const next = new Set(current);
      if (next.has(sectionIndex)) {
        next.delete(sectionIndex);
      } else {
        next.add(sectionIndex);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setCollapsedInteractiveSections(current => {
      const next = new Set<number>();
      current.forEach(index => {
        if (index >= 0 && index < interactiveListDraft.sections.length) {
          next.add(index);
        }
      });
      return next.size === current.size ? current : next;
    });
  }, [interactiveListDraft.sections.length]);

  const addInteractiveRow = useCallback((sectionIndex: number) => {
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.map((section, index) => index === sectionIndex
        ? { ...section, rows: [...section.rows, { id: '', title: '', description: '' }] }
        : section),
    }));
  }, [updateInteractiveList]);

  const removeInteractiveRow = useCallback((sectionIndex: number, rowIndex: number) => {
    updateInteractiveList(current => ({
      ...current,
      sections: current.sections.map((section, index) => index === sectionIndex
        ? { ...section, rows: section.rows.filter((_, candidateIndex) => candidateIndex !== rowIndex) }
        : section),
    }));
  }, [updateInteractiveList]);

  const addInteractiveButton = useCallback(() => {
    updateInteractiveButtons(current => ({
      ...current,
      buttons: isInteractiveButtonAddDisabled(current.buttons.length, interactiveButtonMaxCount)
        ? current.buttons
        : [...current.buttons, { id: '', title: '' }],
    }));
  }, [interactiveButtonMaxCount, updateInteractiveButtons]);

  const removeInteractiveButton = useCallback((buttonIndex: number) => {
    updateInteractiveButtons(current => ({
      ...current,
      buttons: current.buttons.filter((_, candidateIndex) => candidateIndex !== buttonIndex),
    }));
  }, [updateInteractiveButtons]);

  useEffect(() => {
    if (!isScheduleMenuOpen) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      if (!scheduleMenuRef.current?.contains(event.target as Node)) {
        setIsScheduleMenuOpen(false);
        setShowCustomScheduleInput(false);
      }
    };

    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [isScheduleMenuOpen]);

  const handleScheduleAt = async (value: Date) => {
    await onScheduleAt(value);
    setIsScheduleMenuOpen(false);
    setShowCustomScheduleInput(false);
    setCustomScheduleValue('');
  };

  const buildLaterTodayPreset = () => {
    const next = new Date();
    next.setHours(13, 0, 0, 0);
    if (next <= new Date()) {
      next.setDate(next.getDate() + 1);
    }
    return next;
  };

  const buildMondayMorningPreset = () => {
    const now = new Date();
    const daysUntilMonday = (8 - now.getDay()) % 7 || 7;
    const nextMonday = new Date(now);
    nextMonday.setDate(now.getDate() + daysUntilMonday);
    nextMonday.setHours(9, 0, 0, 0);
    return nextMonday;
  };

  if (!isChatOpen) return null;

  return (
    <div className={`${styles.floatingChat} ${chatThemeClassMap[chatTheme]} ${isChatMinimized ? styles.minimized : ''}`}>
      <div className={styles.header}>
        <span className={styles.title}>
          {conversationDisplayTitle}
        </span>
        {scheduledMessages.length > 0 && (
          <span className={styles.scheduledBadge}>{t('scheduledCount', { ns: 'conversations', count: scheduledMessages.length })}</span>
        )}
        <div className={styles.headerButtons}>
          {conversation && (
            <AiVoiceCallControl conversation={conversation} variant="floating" />
          )}
          {conversation && (
            <button
              className={styles.headerBtn}
              onClick={() => {
                navigate(`/conversations/${conversation.id}`);
                setIsChatOpen(false);
              }}
              title={t('openInFullView', { ns: 'conversations' })}
            >
              ⤢
            </button>
          )}
          <button
            className={styles.headerBtn}
            onClick={() => setIsChatMinimized(!isChatMinimized)}
            title={isChatMinimized ? t('expand', { ns: 'conversations' }) : t('minimize', { ns: 'conversations' })}
          >
            {isChatMinimized ? '▲' : '▼'}
          </button>
          <button
            className={styles.headerBtn}
            onClick={() => setIsChatOpen(false)}
            title={t('close', { ns: 'common' })}
          >
            ✕
          </button>
        </div>
      </div>

      {!isChatMinimized && (
        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>{t('loadingConversation', { ns: 'conversations' })}</div>
          ) : !conversation ? (
            <div className={styles.empty}>{t('noConversationSelected', { ns: 'conversations' })}</div>
          ) : (
            <>
              <div className={styles.messagesPane} ref={messagesPaneRef}>
                {conversation.hasMoreMessages ? (
                  <div className={styles.messagesHistoryActions}>
                    <button
                      type="button"
                      className={styles.loadOlderMessagesButton}
                      onClick={() => void loadOlderMessages()}
                      disabled={loadingOlderMessages}
                    >
                      {loadingOlderMessages
                        ? t('loadingOlderMessages', { ns: 'conversations' })
                        : t('loadOlderMessages', { ns: 'conversations' })}
                    </button>
                  </div>
                ) : null}
                {conversation.messages.length === 0 ? (
                  <div className={styles.empty}>{t('noMessagesYet', { ns: 'conversations' })}</div>
                ) : (
                  conversation.messages.map(m => {
                    const isMe = meSender && m.sender.type === meSender.type && m.sender.id === meSender.id;
                    const senderName = getParticipantDisplayName(m.sender);
                    const quotedSenderName = m.quotedMessage ? getParticipantDisplayName(m.quotedMessage.sender) : null;
                    const quotedSnippet = m.quotedMessage ? truncateMessageSnippet(getQuotedMessageSnippet(m.quotedMessage), 90) : null;
                    const quickEmojis = ['👍', '❤️', '😂', '🎉', '👏'];
                    return (
                      <div key={m.id} id={`floating-msg-${m.id}`} className={`${styles.messageRow} ${highlightedMessageId === m.id ? styles.messageRowHighlighted : ''}`} style={{ alignSelf: isMe ? 'flex-end' : 'flex-start' }}>
                        <div className={styles.meta}>
                          <span className={styles.metaPrimary}>
                            <ParticipantAvatar participantType={m.sender.type} className={styles.senderAvatar} />
                            <span>{senderName}</span>
                          </span>
                          <span>{formatTime(m.timestamp)}</span>
                          <span className={styles.messageSourceBadge}>{getMessageSourceContent(m.source)}</span>
                          {m.messageType !== 'text' && (
                            <span className={styles.messageSourceBadge}>{getMessageTypeLabel(m.messageType)}</span>
                          )}
                          {isMe && (
                            <span className={`${styles.statusIcon} ${isOptimisticMessageId(m.id) ? styles.pending : styles[m.status]}`}>
                              {isOptimisticMessageId(m.id) ? renderPendingStatusIcon() : renderStatusIcon(m.status)}
                            </span>
                          )}
                        </div>
                        <div className={styles.messageContainer}>
                          <div className={`${styles.bubble} ${isMe ? styles.me : ''}`}>
                            {m.quotedMessage && quotedSenderName && quotedSnippet ? (
                              <button
                                type="button"
                                className={`${styles.quotedMessagePreview} ${styles.quotedMessagePreviewButton}`}
                                onClick={() => onJumpToMessage(m.quotedMessageId)}
                              >
                                <span className={styles.quotedMessageLabel}>{quotedSenderName}</span>
                                <span className={styles.quotedMessageText}>{quotedSnippet}</span>
                              </button>
                            ) : null}
                            {renderMessageContent(m)}
                            <button
                              type="button"
                              className={styles.quoteBtn}
                              onClick={() => onQuoteMessage(m)}
                              title={t('quoteMessage', { ns: 'conversations' })}
                            >
                              ↩
                            </button>
                            <button 
                              className={styles.reactionBtn}
                              onClick={() => setShowReactionPicker(showReactionPicker === m.id ? null : m.id)}
                              title={t('addReaction', { ns: 'conversations' })}
                            >
                              😊
                            </button>
                          </div>
                          {showReactionPicker === m.id && (
                            <div className={styles.reactionPicker}>
                              {quickEmojis.map(emoji => {
                                const reactionKey = `${conversation.id}:${m.id}:${emoji}`;
                                const isPendingReaction = pendingReactionKeys.has(reactionKey);

                                return (
                                  <button
                                    key={emoji}
                                    className={`${styles.emojiBtn} ${isPendingReaction ? styles.emojiBtnPending : ''}`}
                                    onClick={() => onReaction(m.id, emoji)}
                                    aria-busy={isPendingReaction}
                                  >
                                    {emoji}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          {Object.keys(m.reactions).length > 0 && (
                            <div className={styles.reactionsDisplay}>
                              {Object.entries(m.reactions).map(([emoji, reactors]) => {
                                const reactionKey = `${conversation.id}:${m.id}:${emoji}`;
                                const isPendingReaction = pendingReactionKeys.has(reactionKey);
                                const iReacted = reactors.some(r => meSender && r.type === meSender.type && r.id === meSender.id);
                                const reactorNames = reactors.map(r =>
                                  meSender && r.type === meSender.type && r.id === meSender.id
                                    ? t('you', { ns: 'common', defaultValue: 'You' })
                                    : getParticipantDisplayName(r)
                                );
                                return (
                                  <div key={emoji} className={styles.reactionBadgeWrapper}>
                                    <button
                                      className={`${styles.reactionBadge} ${iReacted ? styles.myReaction : ''} ${isPendingReaction ? styles.reactionBadgePending : ''}`}
                                      onClick={() => onReaction(m.id, emoji)}
                                      aria-busy={isPendingReaction}
                                    >
                                      {emoji} {reactors.length > 1 ? reactors.length : ''}
                                    </button>
                                    <div className={styles.reactionTooltip}>
                                      <span className={styles.reactionTooltipEmoji}>{emoji}</span>
                                      <span className={styles.reactionTooltipNames}>{reactorNames.join(', ')}</span>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              {(aiDraft || aiDraftError) && (
                <div className={styles.aiDraftCard}>
                  <div className={styles.aiDraftHeader}>
                    <span className={styles.aiDraftTitle}>{t('aiReplyDraft', { ns: 'conversations' })}</span>
                    {aiDraft && (
                      <div className={styles.aiDraftActions}>
                        <button
                          type="button"
                          className={styles.aiDraftApplyBtn}
                          onClick={onUseAiDraft}
                        >
                          {t('useDraft', { ns: 'conversations' })}
                        </button>
                        <button
                          type="button"
                          className={styles.aiDraftDismissBtn}
                          onClick={onDismissAiDraft}
                        >
                          {t('close', { ns: 'common' })}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className={styles.aiDraftBody}>{aiDraftError || aiDraft}</div>
                </div>
              )}
              <div
                className={`${styles.composerShell} ${dragActive ? styles.composerShellDrag : ''}`}
                onDragOver={onComposerDragOver}
                onDragLeave={onComposerDragLeave}
                onDrop={onComposerDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  className={styles.hiddenFileInput}
                  accept={ATTACHMENT_ACCEPT}
                  multiple
                  onChange={event => onPickAttachments(event.target.files)}
                />

                {quotedMessage ? (
                  <div className={styles.composerQuotedMessage}>
                    <button
                      type="button"
                      className={styles.composerQuotedMessageJump}
                      onClick={() => onJumpToMessage(quotedMessage.id)}
                    >
                      <div className={styles.composerQuotedMessageBody}>
                        <span className={styles.composerQuotedMessageLabel}>
                          {t('replyingTo', { ns: 'conversations', name: getParticipantDisplayName(quotedMessage.sender) })}
                        </span>
                        <span className={styles.composerQuotedMessageText}>
                          {truncateMessageSnippet(getQuotedMessageSnippet(quotedMessage), 120)}
                        </span>
                      </div>
                    </button>
                    <button
                      type="button"
                      className={styles.composerQuotedMessageClear}
                      onClick={onClearQuotedMessage}
                      title={t('clearQuote', { ns: 'conversations' })}
                    >
                      {t('close', { ns: 'common' })}
                    </button>
                  </div>
                ) : null}

                <div className={styles.composerTools}>
                  <button
                    type="button"
                    className={styles.attachIconBtn}
                    onClick={() => setIsUploaderExpanded(current => !current)}
                    aria-expanded={isUploaderExpanded}
                    aria-controls="floating-uploader-panel"
                  >
                    <svg className={styles.attachIconSvg} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`${styles.audioRecordBtn} ${isRecordingAudio ? styles.audioRecordBtnActive : ''}`}
                    onClick={() => void onAudioRecordToggle()}
                    aria-pressed={isRecordingAudio}
                    title={isRecordingAudio ? t('stopRecordingAudio', { ns: 'conversations' }) : t('startRecordingAudio', { ns: 'conversations' })}
                  >
                    <svg className={styles.audioIconSvg} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M12 15a3 3 0 0 0 3-3V7a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Zm5-3a1 1 0 1 1 2 0 7 7 0 0 1-6 6.93V21h3a1 1 0 1 1 0 2H8a1 1 0 1 1 0-2h3v-2.07A7 7 0 0 1 5 12a1 1 0 1 1 2 0 5 5 0 0 0 10 0Z" />
                    </svg>
                    {isRecordingAudio && <span className={styles.recordDot} aria-hidden="true" />}
                  </button>
                  <ConversationShortcutMenu
                    isOpen={isShortcutMenuOpen}
                    onToggle={() => setIsShortcutMenuOpen(current => !current)}
                    onClose={() => setIsShortcutMenuOpen(false)}
                    onSelectCommand={onShortcutSelectCommand}
                    buttonClassName={styles.shortcutBtn}
                    iconClassName={styles.shortcutIconSvg}
                  />
                  {!isWhatsAppAppLine ? (
                  <button
                    type="button"
                    className={`${styles.interactiveToggleBtn} ${isInteractiveComposerOpen ? styles.interactiveToggleBtnActive : ''} ${!isInteractiveComposerOpen && hasInteractiveDraft ? styles.interactiveToggleBtnDraft : ''}`}
                    onClick={() => setIsInteractiveComposerOpen(current => !current)}
                  >
                    {isInteractiveComposerOpen
                      ? t('interactiveModeActiveButton', { ns: 'conversations' })
                      : hasInteractiveDraft
                        ? t('interactiveDraftButton', { ns: 'conversations' })
                        : t('interactiveComposerButton', { ns: 'conversations' })}
                  </button>
                  ) : null}
                  <button
                    type="button"
                    className={styles.aiDraftBtn}
                    onClick={onGenerateAiDraft}
                    disabled={!meSender || aiDraftLoading || isInteractiveComposerOpen}
                  >
                    {aiDraftLoading ? t('thinking', { ns: 'conversations' }) : t('askAi', { ns: 'conversations' })}
                  </button>
                  {(isRecordingAudio || isUploaderExpanded) && (
                    <span className={styles.dropHint}>
                      {isRecordingAudio
                        ? t('recordingAudioHint', { ns: 'conversations' })
                        : t('uploaderOpenHint', { ns: 'conversations' })}
                    </span>
                  )}
                </div>

                {(isUploaderExpanded || attachments.length > 0 || Boolean(sendError)) && (
                  <div
                    id="floating-uploader-panel"
                    className={`${styles.uploaderPanel} ${styles.uploaderPanelExpanded}`}
                  >
                    <button
                      type="button"
                      className={styles.attachBtn}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {t('chooseFiles', { ns: 'conversations' })}
                    </button>
                    <span className={styles.dropHint}>{t('dropFilesHint', { ns: 'conversations' })}</span>

                    {attachments.length > 0 && (
                      <div className={styles.attachmentList}>
                        {attachments.map((attachment, index) => (
                          <div key={attachment.id} className={styles.attachmentCard}>
                            <div className={styles.attachmentPreview}>
                              {attachment.kind === 'image' && attachment.previewUrl && (
                                <img className={styles.attachmentImagePreview} src={attachment.previewUrl} alt={attachment.file.name} />
                              )}
                              {attachment.kind === 'audio' && attachment.previewUrl && (
                                <audio className={styles.mediaPlayer} controls preload="metadata" src={attachment.previewUrl} />
                              )}
                              {attachment.kind === 'video' && attachment.previewUrl && (
                                <video className={styles.mediaPlayer} controls preload="metadata" src={attachment.previewUrl} />
                              )}
                              {attachment.kind === 'document' && (
                                <div className={styles.attachmentIcon} title={t('documentAttachment', { ns: 'conversations' })}>
                                  <svg className={styles.attachmentIconSvg} viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
                                    <path d="M7 3.5h7l4 4V20a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 20V5A1.5 1.5 0 0 1 7.5 3.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    <path d="M14 3.5V8h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                                    <path d="M9 12h6M9 15h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                                  </svg>
                                </div>
                              )}
                            </div>

                            <div className={styles.attachmentMeta}>
                              <div className={styles.attachmentName}>{attachment.file.name}</div>
                              <div className={styles.attachmentInfo}>
                                {attachment.kind === 'image'
                                  ? t('imageAttachment', { ns: 'conversations' })
                                  : attachment.kind === 'audio'
                                    ? t('audioAttachment', { ns: 'conversations' })
                                    : attachment.kind === 'video'
                                      ? t('videoAttachment', { ns: 'conversations' })
                                      : t('documentAttachment', { ns: 'conversations' })} • {formatBytes(attachment.file.size)}{index === 0 && composer.trim() ? ` • ${t('includesCaption', { ns: 'conversations' })}` : ''}
                              </div>
                            </div>

                            <button
                              type="button"
                              className={styles.clearAttachmentBtn}
                              onClick={() => removeAttachment(attachment.id)}
                            >
                              {t('removeAttachment', { ns: 'conversations' })}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {sendError && <div className={styles.sendError}>{sendError}</div>}
                  </div>
                )}

                <div className={styles.composer}>
                  <textarea
                    ref={composerInputRef}
                    className={styles.input}
                    placeholder={isInteractiveComposerOpen
                      ? t('interactiveComposerMessagePlaceholder', { ns: 'conversations' })
                      : attachments.length > 1
                        ? t('addCaptionFirstOptional', { ns: 'conversations' })
                        : attachments.length === 1
                          ? t('addCaptionOptional', { ns: 'conversations' })
                          : t('typeMessageOrAskAi', { ns: 'conversations' })}
                    rows={1}
                    value={composer}
                    onChange={event => onComposerChange(event.target.value, event.target.selectionStart ?? event.target.value.length)}
                    onClick={event => onComposerSelectionChange((event.currentTarget as HTMLTextAreaElement).selectionStart ?? composer.length)}
                    onKeyUp={event => onComposerSelectionChange((event.currentTarget as HTMLTextAreaElement).selectionStart ?? composer.length)}
                    onKeyDown={e => {
                      if (onComposerKeyDown(e)) {
                        return;
                      }

                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        void onSend();
                      }
                    }}
                  />
                  {mentionMenuOpen && mentionSuggestions.length > 0 && (
                    <div className={styles.mentionMenu} role="listbox" aria-label={t('participants', { ns: 'conversations' })}>
                      {mentionSuggestions.map((option, index) => {
                        const isActive = mentionActiveIndex === index;
                        return (
                          <button
                            key={`${option.type}:${option.id}`}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`${styles.mentionMenuItem} ${isActive ? styles.mentionMenuItemActive : ''}`}
                            onMouseDown={event => {
                              event.preventDefault();
                              onSelectMention(option);
                            }}
                          >
                            <ParticipantAvatar participantType={option.type} className={styles.mentionMenuAvatar} />
                            <span className={styles.mentionMenuText}>
                              <span className={styles.mentionMenuName}>{option.name}</span>
                              {option.email ? <span className={styles.mentionMenuMeta}>{option.email}</span> : <span className={styles.mentionMenuMeta}>{option.type}</span>}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {commandMenuOpen && commandSuggestions.length > 0 && (
                    <div className={styles.mentionMenu} role="listbox" aria-label={t('tabCommands', { ns: 'tools' })}>
                      {commandSuggestions.map((command, index) => {
                        const isActive = commandActiveIndex === index;
                        return (
                          <button
                            key={command.commandId}
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            className={`${styles.mentionMenuItem} ${isActive ? styles.mentionMenuItemActive : ''}`}
                            onMouseDown={event => {
                              event.preventDefault();
                              onSelectCommand(command);
                            }}
                          >
                            <span className={styles.mentionMenuText}>
                              <span className={styles.mentionMenuName}>/{command.trigger}</span>
                              <span className={styles.mentionMenuMeta}>
                                {command.displayName}
                                {command.description ? ` — ${command.description}` : ''}
                              </span>
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <div className={styles.sendSplit} ref={scheduleMenuRef}>
                    <button
                      className={styles.sendSplitPrimary}
                      onClick={() => void onSend()}
                      disabled={!canSend}
                      aria-label={t('sendMessage', { ns: 'conversations' })}
                    >
                      <svg className={styles.sendIcon} viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 12h13M12 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className={styles.sendSplitMenu}
                      onClick={() => setIsScheduleMenuOpen(open => !open)}
                      disabled={scheduleDisabled}
                      aria-label={t('scheduleMessage', { ns: 'conversations' })}
                    >
                      ▾
                    </button>

                    {isScheduleMenuOpen && (
                      <div className={styles.scheduleMenu}>
                        <div className={styles.scheduleMenuTitle}>{t('scheduleMessage', { ns: 'conversations' })}</div>
                        <button
                          type="button"
                          className={styles.scheduleMenuItem}
                          onClick={() => void handleScheduleAt(buildLaterTodayPreset())}
                        >
                          {t('laterTodayPreset', { ns: 'conversations' })}
                        </button>
                        <button
                          type="button"
                          className={styles.scheduleMenuItem}
                          onClick={() => void handleScheduleAt(buildMondayMorningPreset())}
                        >
                          {t('mondayMorningPreset', { ns: 'conversations' })}
                        </button>
                        <div className={styles.scheduleMenuDivider} />
                        <button
                          type="button"
                          className={styles.scheduleMenuItem}
                          onClick={() => {
                            setShowCustomScheduleInput(true);
                            if (!customScheduleValue) {
                              const seed = scheduledAt ?? new Date(Date.now() + 30 * 60 * 1000);
                              setCustomScheduleValue(formatDateTimeInputValue(seed));
                            }
                          }}
                        >
                          {t('customTime', { ns: 'conversations' })}
                        </button>

                        {showCustomScheduleInput && (
                          <div className={styles.scheduleCustomRow}>
                            <input
                              type="datetime-local"
                              className={styles.scheduledInput}
                              value={customScheduleValue}
                              onChange={event => setCustomScheduleValue(event.target.value)}
                            />
                            <button
                              type="button"
                              className={styles.scheduleApplyBtn}
                              onClick={() => {
                                if (!customScheduleValue) {
                                  return;
                                }
                                const parsedScheduleTime = parseDateTimeInputValue(customScheduleValue);
                                if (!parsedScheduleTime) {
                                  return;
                                }
                                void handleScheduleAt(parsedScheduleTime);
                              }}
                            >
                              {t('schedule', { ns: 'conversations' })}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                {scheduleError && <div className={styles.sendError}>{scheduleError}</div>}
              </div>

              {isInteractiveComposerOpen && !isWhatsAppAppLine && (
                <div
                  className={styles.modalOverlay}
                  role="dialog"
                  aria-modal="true"
                  aria-label={t('interactiveComposerTitle', { ns: 'conversations' })}
                >
                  <div className={`${styles.modalCard} ${styles.interactiveModalCard}`}>
                    <div className={styles.modalHeader}>
                      <div>
                        <div className={styles.modalTitle}>{t('interactiveComposerTitle', { ns: 'conversations' })}</div>
                        <div className={styles.interactiveComposerHint}>{t('interactiveComposerHint', { ns: 'conversations' })}</div>
                      </div>
                      <button
                        type="button"
                        className={styles.modalCloseBtn}
                        onClick={() => setIsInteractiveComposerOpen(false)}
                      >
                        {t('close', { ns: 'common' })}
                      </button>
                    </div>

                    <div className={styles.interactiveComposerCard}>
                      <div className={styles.interactiveModeTabs}>
                        <button
                          type="button"
                          className={`${styles.interactiveModeTab} ${interactiveComposerMode === 'list' ? styles.interactiveModeTabActive : ''}`}
                          onClick={() => setInteractiveComposerMode('list')}
                        >
                          {t('interactiveComposerModeList', { ns: 'conversations' })}
                        </button>
                        <button
                          type="button"
                          className={`${styles.interactiveModeTab} ${interactiveComposerMode === 'buttons' ? styles.interactiveModeTabActive : ''}`}
                          onClick={() => setInteractiveComposerMode('buttons')}
                        >
                          {t('interactiveComposerModeButtons', { ns: 'conversations' })}
                        </button>
                      </div>
                      <div className={styles.interactiveComposerStats}>
                        {interactiveComposerMode === 'list' ? (
                          <>
                            <span className={styles.interactiveComposerStat}>{t('interactiveSectionsValue', { ns: 'conversations', count: interactiveSectionCount })}</span>
                            <span className={styles.interactiveComposerStat}>{t('interactiveOptionsValue', { ns: 'conversations', count: interactiveOptionCount })}</span>
                          </>
                        ) : (
                          <span className={styles.interactiveComposerStat}>{t('interactiveButtonsValue', { ns: 'conversations', count: interactiveButtonCount })}</span>
                        )}
                      </div>

                      <div className={styles.interactiveComposerScrollArea}>
                        <div className={styles.interactiveFieldGrid}>
                          {interactiveComposerMode === 'list' ? (
                            <>
                              <label className={styles.interactiveField}>
                                <span>{t('interactiveButton', { ns: 'conversations' })}</span>
                                <input
                                  className={styles.interactiveInput}
                                  value={interactiveListDraft.buttonText}
                                  onChange={event => updateInteractiveList(current => ({ ...current, buttonText: event.target.value }))}
                                  placeholder={t('interactiveButtonPlaceholder', { ns: 'conversations' })}
                                />
                              </label>
                              <label className={`${styles.interactiveField} ${styles.interactiveFieldFull}`}>
                                <span>{t('interactiveBody', { ns: 'conversations' })}</span>
                                <textarea
                                  className={styles.interactiveTextarea}
                                  rows={3}
                                  value={interactiveListDraft.bodyText}
                                  onChange={event => updateInteractiveList(current => ({ ...current, bodyText: event.target.value }))}
                                  placeholder={t('interactiveBodyPlaceholder', { ns: 'conversations' })}
                                />
                              </label>
                            </>
                          ) : (
                            <label className={`${styles.interactiveField} ${styles.interactiveFieldFull}`}>
                              <span>{t('interactiveBody', { ns: 'conversations' })}</span>
                              <textarea
                                className={styles.interactiveTextarea}
                                rows={3}
                                value={interactiveButtonsDraft.bodyText}
                                onChange={event => updateInteractiveButtons(current => ({ ...current, bodyText: event.target.value }))}
                                placeholder={t('interactiveButtonsBodyPlaceholder', { ns: 'conversations' })}
                              />
                            </label>
                          )}
                        </div>

                        <details className={styles.interactiveAdvancedPanel}>
                          <summary className={styles.interactiveAdvancedSummary}>{t('interactiveOptionalSettings', { ns: 'conversations' })}</summary>
                          <div className={styles.interactiveFieldGrid}>
                            <label className={styles.interactiveField}>
                              <span>{t('interactiveHeader', { ns: 'conversations' })}</span>
                              <input
                                className={styles.interactiveInput}
                                value={interactiveComposerMode === 'list' ? (interactiveListDraft.headerText || '') : (interactiveButtonsDraft.headerText || '')}
                                onChange={event => interactiveComposerMode === 'list'
                                  ? updateInteractiveList(current => ({ ...current, headerText: event.target.value }))
                                  : updateInteractiveButtons(current => ({ ...current, headerText: event.target.value }))}
                                placeholder={t('interactiveHeaderPlaceholder', { ns: 'conversations' })}
                              />
                            </label>
                            <label className={styles.interactiveField}>
                              <span>{t('interactiveFooter', { ns: 'conversations' })}</span>
                              <input
                                className={styles.interactiveInput}
                                value={interactiveComposerMode === 'list' ? (interactiveListDraft.footerText || '') : (interactiveButtonsDraft.footerText || '')}
                                onChange={event => interactiveComposerMode === 'list'
                                  ? updateInteractiveList(current => ({ ...current, footerText: event.target.value }))
                                  : updateInteractiveButtons(current => ({ ...current, footerText: event.target.value }))}
                                placeholder={t('interactiveFooterPlaceholder', { ns: 'conversations' })}
                              />
                            </label>
                          </div>
                        </details>

                        {interactiveComposerMode === 'list' ? (
                          <div className={styles.interactiveSectionsList}>
                            {interactiveListDraft.sections.map((section, sectionIndex) => (
                              <div key={`floating-composer-section-${sectionIndex}`} className={styles.interactiveSectionCard}>
                                <div className={styles.interactiveSectionHeader}>
                                  <div className={styles.interactiveSectionMeta}>
                                    <strong>{section.title?.trim() || t('interactiveSection', { ns: 'conversations', index: sectionIndex + 1 })}</strong>
                                    <span>{t('interactiveOptionsValue', { ns: 'conversations', count: section.rows.length })}</span>
                                  </div>
                                  <div className={styles.interactiveSectionControls}>
                                    <button
                                      type="button"
                                      className={styles.interactiveSectionToggle}
                                      onClick={() => toggleInteractiveSection(sectionIndex)}
                                    >
                                      {collapsedInteractiveSections.has(sectionIndex)
                                        ? t('expand', { ns: 'conversations' })
                                        : t('minimize', { ns: 'conversations' })}
                                    </button>
                                    {interactiveListDraft.sections.length > 1 && (
                                      <button
                                        type="button"
                                        className={styles.interactiveGhostBtn}
                                        onClick={() => removeInteractiveSection(sectionIndex)}
                                      >
                                        {t('removeSection', { ns: 'conversations' })}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {!collapsedInteractiveSections.has(sectionIndex) && (
                                  <div className={styles.interactiveSectionBody}>
                                    <label className={`${styles.interactiveField} ${styles.interactiveFieldFull}`}>
                                      <span>{t('interactiveSection', { ns: 'conversations', index: sectionIndex + 1 })}</span>
                                      <input
                                        className={styles.interactiveInput}
                                        value={section.title || ''}
                                        onChange={event => updateInteractiveList(current => ({
                                          ...current,
                                          sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                            ? { ...candidateSection, title: event.target.value }
                                            : candidateSection),
                                        }))}
                                        placeholder={t('interactiveSectionPlaceholder', { ns: 'conversations' })}
                                      />
                                    </label>

                                    <div className={styles.interactiveRowsList}>
                                      {section.rows.map((row, rowIndex) => (
                                        <div key={`floating-composer-row-${sectionIndex}-${rowIndex}`} className={styles.interactiveRowCard}>
                                          <div className={styles.interactiveRowGrid}>
                                            <label className={styles.interactiveField}>
                                              <span>{t('interactiveRowTitle', { ns: 'conversations' })}</span>
                                              <input
                                                className={styles.interactiveInput}
                                                value={row.title}
                                                onChange={event => updateInteractiveList(current => ({
                                                  ...current,
                                                  sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                                    ? {
                                                        ...candidateSection,
                                                        rows: candidateSection.rows.map((candidateRow, candidateRowIndex) => candidateRowIndex === rowIndex
                                                          ? { ...candidateRow, title: event.target.value }
                                                          : candidateRow),
                                                      }
                                                    : candidateSection),
                                                }))}
                                                placeholder={t('interactiveRowTitlePlaceholder', { ns: 'conversations' })}
                                              />
                                            </label>
                                          </div>
                                          <label className={`${styles.interactiveField} ${styles.interactiveFieldFull}`}>
                                            <span>{t('interactiveRowDescription', { ns: 'conversations' })}</span>
                                            <input
                                              className={styles.interactiveInput}
                                              value={row.description || ''}
                                              onChange={event => updateInteractiveList(current => ({
                                                ...current,
                                                sections: current.sections.map((candidateSection, candidateIndex) => candidateIndex === sectionIndex
                                                  ? {
                                                      ...candidateSection,
                                                      rows: candidateSection.rows.map((candidateRow, candidateRowIndex) => candidateRowIndex === rowIndex
                                                        ? { ...candidateRow, description: event.target.value }
                                                        : candidateRow),
                                                    }
                                                  : candidateSection),
                                              }))}
                                              placeholder={t('interactiveRowDescriptionPlaceholder', { ns: 'conversations' })}
                                            />
                                          </label>
                                          {section.rows.length > 1 && (
                                            <button
                                              type="button"
                                              className={styles.interactiveGhostBtn}
                                              onClick={() => removeInteractiveRow(sectionIndex, rowIndex)}
                                            >
                                              {t('removeRow', { ns: 'conversations' })}
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>

                                    <button
                                      type="button"
                                      className={styles.interactiveSecondaryBtn}
                                      onClick={() => addInteractiveRow(sectionIndex)}
                                    >
                                      {t('addRow', { ns: 'conversations' })}
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={styles.interactiveSectionsList}>
                            {interactiveButtonsDraft.buttons.map((button, buttonIndex) => (
                              <div key={`floating-composer-button-${buttonIndex}`} className={styles.interactiveRowCard}>
                                <div className={styles.interactiveRowGrid}>
                                  <label className={styles.interactiveField}>
                                    <span>{t('interactiveReplyButtonTitle', { ns: 'conversations' })}</span>
                                    <input
                                      className={styles.interactiveInput}
                                      value={button.title}
                                      onChange={event => updateInteractiveButtons(current => ({
                                        ...current,
                                        buttons: current.buttons.map((candidateButton, candidateIndex) => candidateIndex === buttonIndex
                                          ? { ...candidateButton, title: event.target.value }
                                          : candidateButton),
                                      }))}
                                      placeholder={t('interactiveReplyButtonTitlePlaceholder', { ns: 'conversations' })}
                                    />
                                  </label>
                                </div>
                                {interactiveButtonsDraft.buttons.length > 1 && (
                                  <button
                                    type="button"
                                    className={styles.interactiveGhostBtn}
                                    onClick={() => removeInteractiveButton(buttonIndex)}
                                  >
                                    {t('removeInteractiveReplyButton', { ns: 'conversations' })}
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {sendError && <div className={styles.sendError}>{sendError}</div>}

                      <div className={styles.interactiveComposerFooter}>
                        <button
                          type="button"
                          className={styles.interactiveSecondaryBtn}
                          onClick={interactiveComposerMode === 'list' ? addInteractiveSection : addInteractiveButton}
                          disabled={interactiveComposerMode === 'buttons' && isInteractiveButtonAddAtLimit}
                        >
                          {interactiveComposerMode === 'list'
                            ? t('addSection', { ns: 'conversations' })
                            : t('addInteractiveReplyButton', { ns: 'conversations' })}
                        </button>
                        <button
                          type="button"
                          className={styles.sendSplitPrimary}
                          onClick={() => void onSend()}
                          disabled={!canSend}
                        >
                          {t('sendMessage', { ns: 'conversations' })}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {maximizedMedia && (
                <div
                  className={styles.imageViewerOverlay}
                  role="dialog"
                  aria-modal="true"
                  aria-label={maximizedMedia.type === 'image' ? t('imageMessage', { ns: 'conversations' }) : t('openVideo', { ns: 'conversations' })}
                  onClick={() => setMaximizedMedia(null)}
                >
                  <button
                    type="button"
                    className={styles.imageViewerCloseButton}
                    onClick={() => setMaximizedMedia(null)}
                  >
                    {t('close', { ns: 'common' })}
                  </button>
                  {maximizedMedia.type === 'image' ? (
                    <img
                      className={styles.imageViewerImage}
                      src={maximizedMedia.src}
                      alt={maximizedMedia.alt || t('imageMessage', { ns: 'conversations' })}
                      onClick={event => event.stopPropagation()}
                    />
                  ) : (
                    <video
                      className={styles.imageViewerVideo}
                      src={maximizedMedia.src}
                      controls
                      autoPlay
                      onClick={event => event.stopPropagation()}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export { FloatingChat };
export default FloatingChat;
